import { useCallback, useEffect, useMemo, useState } from "react";
import { BsCheckCircle, BsCardChecklist, BsXOctagon } from "react-icons/bs";
import { IoMdArrowDropright } from "react-icons/io";
import { LuEye, LuPaperclip } from "react-icons/lu";
import { PiTrash } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import CustomModal from "../../../components/CustomModal";
import CustomTabs from "../../../components/CustomTabs";
import NewTableComponent from "../../../components/NewTableComponent";
import Stats from "../../../components/Stats";
import UserAvatar from "../../../components/UserAvatar";
import ConfirmDeleteModal from "../../../components/modals/ConfirmDeleteModal";
import receiptPreviewImage from "../../../assets/images/refuel_img.png";
import { apiService } from "../../../services/apiService";
import "../../../styles/Access.css";
import "../../../styles/Procurement.css";
import "../../../styles/Services.css";
import "../../../styles/Table.css";
import "../../../styles/Trip.css";
import "../../../styles/TripCostEstimation.css";
import "../../../styles/DriverExpense.css";
import {
  activeRFQ,
  coinsStackIcon,
  draftIcon,
  partValueIcon,
  RFQIcon,
  totalBidIcon,
} from "../../../utils/svgs";
import { errorNotify, successNotify } from "../../../utils/toast";

const DRIVER_EXPENSE_APPROVALS_TAB = "driver-expense-approvals";
const EXPENSE_LEDGER_TAB = "expense-ledger";
const DEFAULT_LIMIT = 10;
const FETCH_ALL_LIMIT = 10;

const statusOptions = [
  { label: "Approved", value: "Approved" },
  { label: "Pending", value: "Pending" },
  { label: "Rejected", value: "Rejected" },
];

const formatNumber = (value) =>
  new Intl.NumberFormat("en-PK", {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const normalizeText = (value) => String(value ?? "").trim().toLowerCase();

const safeArray = (value) => (Array.isArray(value) ? value : []);

const matchesSearchTerm = (value, searchText) => {
  if (!normalizeText(searchText)) return true;
  return normalizeText(value).includes(normalizeText(searchText));
};

const toComparableDateValue = (value) => {
  if (!value) return Number.NaN;
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? Number.NaN : parsedDate.getTime();
};

const matchesFilter = (rowValue, filterMeta = {}) => {
  const filterType = filterMeta?.filter_type;
  const filterValue = filterMeta?.value;
  const rowValues = Array.isArray(rowValue) ? rowValue : [rowValue];
  const normalizedRowValues = rowValues.map((value) => normalizeText(value));

  if (!filterType || filterValue === undefined || filterValue === null || filterValue === "") {
    return true;
  }

  if (filterType === "one_of") {
    const filterValues = safeArray(filterValue).map((value) => normalizeText(value));
    return normalizedRowValues.some((value) => filterValues.includes(value));
  }

  if (filterType === "contains") {
    return normalizedRowValues.some((value) => value.includes(normalizeText(filterValue)));
  }

  if (filterType === "equals" || filterType === "equal_to") {
    const normalizedFilterValue = normalizeText(filterValue);
    return normalizedRowValues.some((value) => value === normalizedFilterValue);
  }

  const comparableRowValue = rowValues[0];
  const rowDateValue = toComparableDateValue(comparableRowValue);
  const filterDateValue = toComparableDateValue(filterValue);

  if (!Number.isNaN(rowDateValue) && !Number.isNaN(filterDateValue)) {
    if (filterType === "less_than") return rowDateValue < filterDateValue;
    if (filterType === "greater_than") return rowDateValue > filterDateValue;
    if (filterType === "less_than_equal_to") return rowDateValue <= filterDateValue;
    if (filterType === "greater_than_equal_to") return rowDateValue >= filterDateValue;
  }

  const rowNumberValue = Number(comparableRowValue);
  const filterNumberValue = Number(filterValue);

  if (Number.isFinite(rowNumberValue) && Number.isFinite(filterNumberValue)) {
    if (filterType === "less_than") return rowNumberValue < filterNumberValue;
    if (filterType === "greater_than") return rowNumberValue > filterNumberValue;
    if (filterType === "less_than_equal_to") return rowNumberValue <= filterNumberValue;
    if (filterType === "greater_than_equal_to") return rowNumberValue >= filterNumberValue;
  }

  return true;
};

const humanizeText = (value = "") => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) return "-";

  return normalizedValue
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

const formatDisplayDate = (value) => {
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "-";

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatStatusLabel = (value) => {
  const normalizedValue = normalizeText(value);
  if (!normalizedValue) return "Pending";
  return humanizeText(normalizedValue);
};

const buildPreviewAttachment = (file, fallbackKey) => {
  const filePath = file?.file_path || file?.path || "";
  const fileName = filePath ? filePath.split("/").pop() : `attachment-${fallbackKey}`;

  return {
    id: file?.id || `${fallbackKey}-${fileName}`,
    name: fileName,
    preview: filePath
      ? `${process.env.REACT_APP_API_FILES_URL}${filePath}`
      : receiptPreviewImage,
  };
};

const getDaysBetween = (startValue, endValue) => {
  const startTime = new Date(startValue).getTime();
  const endTime = new Date(endValue).getTime();

  if (Number.isNaN(startTime) || Number.isNaN(endTime)) {
    return 0;
  }

  return Math.max(1, Math.ceil((endTime - startTime) / (1000 * 60 * 60 * 24)));
};

const formatDurationLabel = (trip = {}) => {
  const startValue = trip?.actual_start_at || trip?.start_at || trip?.created_at;
  const endValue =
    trip?.actual_end_at || trip?.expected_end_at || trip?.updated_at || trip?.created_at;
  const totalDays = getDaysBetween(startValue, endValue);

  if (!totalDays) return "-";
  return `${totalDays} Day${totalDays === 1 ? "" : "s"}`;
};

const getCustomerName = (customer) => {
  const customerList = safeArray(customer);
  if (customerList.length > 0) {
    return customerList[0]?.name || "-";
  }

  return customer?.name || "-";
};

const getApprovalSubCategory = (entry = {}) => {
  if (entry?.filling_station?.name) return entry.filling_station.name;
  if (getCustomerName(entry?.customer) !== "-") return getCustomerName(entry.customer);
  if (entry?.location) return entry.location;
  if (entry?.payment_mode) return humanizeText(entry.payment_mode);
  return "-";
};

const sortByNewest = (items = [], accessor) =>
  [...safeArray(items)].sort((firstItem, secondItem) => {
    const firstValue = new Date(accessor(firstItem) || 0).getTime();
    const secondValue = new Date(accessor(secondItem) || 0).getTime();
    return secondValue - firstValue;
  });

const buildApprovalRows = (approvalEntries = [], ledgerEntries = []) => {
  const ledgerByTripId = safeArray(ledgerEntries).reduce((accumulator, item) => {
    const tripKey = String(item?.trip_id || item?.trip?.id || "");
    if (!tripKey) return accumulator;

    if (!accumulator[tripKey]) {
      accumulator[tripKey] = [];
    }

    accumulator[tripKey].push(item);
    return accumulator;
  }, {});

  return sortByNewest(approvalEntries, (entry) => entry?.assign_at || entry?.created_at).map(
    (entry) => {
      const tripKey = String(entry?.trip_id || entry?.trip?.id || "");
      const relatedLedgerEntries = sortByNewest(
        ledgerByTripId[tripKey] || [],
        (ledgerEntry) => ledgerEntry?.created_at
      );
      const driverName =
        relatedLedgerEntries[0]?.driver?.username ||
        (entry?.trip?.primary_driver_id ? `Driver #${entry.trip.primary_driver_id}` : "-");

      return {
        id: entry?.id,
        tripRecordId: Number(entry?.trip_id || entry?.trip?.id || 0) || null,
        tripId: entry?.trip?.trip_id || (tripKey ? `TR-${tripKey}` : "-"),
        customerName: getCustomerName(entry?.customer),
        driverName,
        driverImage: "",
        vehicleId:
          entry?.vehicle?.vehicle_id ||
          entry?.vehicle?.registration_number ||
          entry?.vehicle?.registration_number_slug ||
          "-",
        category: humanizeText(entry?.source || "petty-cash"),
        subCategory: getApprovalSubCategory(entry),
        totalAmount: Number(entry?.amount || 0),
        expenseDuration: formatDurationLabel(entry?.trip),
        expenseDateDisplay: formatDisplayDate(entry?.assign_at || entry?.created_at),
        expenseDateValue: entry?.assign_at || entry?.created_at || "",
        status: formatStatusLabel(entry?.status),
        attachments: safeArray(entry?.files).map((file, index) =>
          buildPreviewAttachment(file, `${entry?.id || "approval"}-${index + 1}`)
        ),
        logs: relatedLedgerEntries.map((ledgerEntry) => ({
          id: ledgerEntry?.id,
          category:
            ledgerEntry?.expense_category?.title ||
            humanizeText(ledgerEntry?.trip_expense_category),
          expenseDateDisplay: formatDisplayDate(ledgerEntry?.created_at),
          expenseDateValue: ledgerEntry?.created_at || "",
          totalAmount: Number(ledgerEntry?.amount || 0),
          attachments: safeArray(ledgerEntry?.files).map((file, index) =>
            buildPreviewAttachment(file, `${ledgerEntry?.id || "ledger"}-${index + 1}`)
          ),
        })),
      };
    }
  );
};

const buildLedgerRows = (ledgerEntries = []) =>
  sortByNewest(ledgerEntries, (entry) => entry?.created_at).map((entry) => ({
    id: entry?.id,
    ledgerEntryId: String(entry?.id ?? "-"),
    sourceModule: "Driver Expense",
    sourceRecordId: String(entry?.expense_category?.id ?? entry?.id ?? "-"),
    tripId: entry?.trip?.trip_id || "-",
    vehicleId:
      entry?.vehicle?.vehicle_id ||
      entry?.vehicle?.registration_number ||
      entry?.vehicle?.registration_number_slug ||
      "-",
    expenseCategory:
      entry?.expense_category?.title || humanizeText(entry?.trip_expense_category),
    subCategory: humanizeText(entry?.trip_expense_category),
    amount: Number(entry?.amount || 0),
    expenseDateDisplay: formatDisplayDate(entry?.created_at),
    expenseDateValue: entry?.created_at || "",
  }));

const buildLedgerStatsData = (ledgerEntries = [], approvalEntries = []) => {
  const ledgerAmountTotal = safeArray(ledgerEntries).reduce(
    (sum, entry) => sum + Number(entry?.amount || 0),
    0
  );
  const fuelAmountTotal = safeArray(approvalEntries)
    .filter((entry) => normalizeText(entry?.source) === "filling station")
    .reduce((sum, entry) => sum + Number(entry?.amount || 0), 0);
  const driverExpenseTotal = safeArray(ledgerEntries).reduce(
    (sum, entry) => sum + Number(entry?.amount || 0),
    0
  );
  const maintenancePartsTotal = safeArray(ledgerEntries)
    .filter((entry) =>
      ["maintenance", "parts", "repair", "service", "tyre"].includes(
        normalizeText(entry?.trip_expense_category)
      )
    )
    .reduce((sum, entry) => sum + Number(entry?.amount || 0), 0);

  return [
    {
      title: "Total Expense (PKR)",
      value: formatNumber(ledgerAmountTotal),
      text: <span>Total Operational Expenses</span>,
      className: "pending-delivery",
      icon: coinsStackIcon,
    },
    {
      title: "Fuel Expenses (PKR)",
      value: formatNumber(fuelAmountTotal),
      text: <span>Total Fuel Costs</span>,
      icon: totalBidIcon,
    },
    {
      title: "Driver Expenses (PKR)",
      value: formatNumber(driverExpenseTotal),
      text: <span>Total Driver Petty Expenses</span>,
      className: "completed",
      icon: activeRFQ,
    },
    {
      title: "Maintenance & Parts (PKR)",
      value: formatNumber(maintenancePartsTotal),
      text: <span>Total Maintenance Costs</span>,
      className: "late",
      icon: partValueIcon,
    },
  ];
};

const buildApprovalStatsData = (approvalEntries = []) => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const approvedCount = safeArray(approvalEntries).filter(
    (entry) => normalizeText(entry?.status) === "approved"
  ).length;
  const pendingCount = safeArray(approvalEntries).filter(
    (entry) => normalizeText(entry?.status) === "pending"
  ).length;
  const rejectedCount = safeArray(approvalEntries).filter(
    (entry) => normalizeText(entry?.status) === "rejected"
  ).length;
  const currentMonthExpenseTotal = safeArray(approvalEntries)
    .filter((entry) => {
      const referenceDate = new Date(entry?.assign_at || entry?.created_at || "");
      return (
        !Number.isNaN(referenceDate.getTime()) &&
        referenceDate.getMonth() === currentMonth &&
        referenceDate.getFullYear() === currentYear
      );
    })
    .reduce((sum, entry) => sum + Number(entry?.amount || 0), 0);

  return [
    {
      title: "Approved Expenses",
      value: formatNumber(approvedCount),
      text: <span>Approved Expenses</span>,
      icon: activeRFQ,
    },
    {
      title: "Pending Expenses",
      value: formatNumber(pendingCount),
      text: <span>Awaiting Finance Review</span>,
      className: "completed",
      icon: totalBidIcon,
    },
    {
      title: "Rejected Expenses",
      value: formatNumber(rejectedCount),
      text: <span>Rejected By Finance</span>,
      className: "late",
      icon: draftIcon,
    },
    {
      title: "Total Monthly Expense (PKR)",
      value: formatNumber(currentMonthExpenseTotal),
      text: <span>Current Month Approved Expense</span>,
      className: "pending-delivery",
      icon: RFQIcon,
    },
  ];
};

const resolvePaginatedPayload = (response = {}) => response?.data || response || {};

const fetchAllPaginatedResults = async (fetchPage) => {
  const collectedResults = [];
  let nextPage = 1;
  let totalPages = 1;

  while (nextPage <= totalPages) {
    const response = await fetchPage({ page: nextPage, limit: FETCH_ALL_LIMIT });
    const payload = resolvePaginatedPayload(response);

    collectedResults.push(...safeArray(payload?.results));

    const resolvedTotalPages = Number(payload?.meta?.totalPages || 1);
    totalPages = Number.isFinite(resolvedTotalPages) && resolvedTotalPages > 0 ? resolvedTotalPages : 1;
    nextPage += 1;
  }

  return collectedResults;
};

const driverExpenseTabs = [
  {
    key: DRIVER_EXPENSE_APPROVALS_TAB,
    label: "Driver Expense Approvals",
    icon: null,
  },
  {
    key: EXPENSE_LEDGER_TAB,
    label: "Expense Ledger",
    icon: null,
  },
];

const approvalStatusMeta = {
  Approved: {
    className: "approved",
    icon: <BsCheckCircle />,
  },
  Pending: {
    className: "pending",
    icon: <BsCardChecklist />,
  },
  Rejected: {
    className: "rejected",
    icon: <BsXOctagon />,
  },
};

const DriverExpense = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(DRIVER_EXPENSE_APPROVALS_TAB);
  const [approvalEntries, setApprovalEntries] = useState([]);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [previewAttachment, setPreviewAttachment] = useState(null);

  const fetchExpenseData = useCallback(async () => {
    setLoading(true);

    const [approvalsResult, ledgerResult] = await Promise.allSettled([
      fetchAllPaginatedResults((params) => apiService.getDriverExpenseApprovalsPaginated(params)),
      fetchAllPaginatedResults((params) => apiService.getExpenseLedgerPaginated(params)),
    ]);

    if (approvalsResult.status === "fulfilled") {
      setApprovalEntries(approvalsResult.value);
    } else {
      setApprovalEntries([]);
      errorNotify(approvalsResult.reason || "Failed to fetch driver expense approvals");
    }

    if (ledgerResult.status === "fulfilled") {
      setLedgerEntries(ledgerResult.value);
    } else {
      setLedgerEntries([]);
      errorNotify(ledgerResult.reason || "Failed to fetch expense ledger");
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchExpenseData();
  }, [fetchExpenseData]);

  useEffect(() => {
    setSearchText("");
    setSearchFilters({});
    setCurrentPage(1);
    setLimit(DEFAULT_LIMIT);
  }, [activeTab]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, searchFilters, limit]);

  const approvalRows = useMemo(
    () => buildApprovalRows(approvalEntries, ledgerEntries),
    [approvalEntries, ledgerEntries]
  );
  const ledgerRows = useMemo(() => buildLedgerRows(ledgerEntries), [ledgerEntries]);

  const currentTabLabel = useMemo(
    () =>
      driverExpenseTabs.find((tab) => tab.key === activeTab)?.label || "Expenses",
    [activeTab]
  );

  const currentRows = activeTab === EXPENSE_LEDGER_TAB ? ledgerRows : approvalRows;

  const handleOpenApprovalDetail = useCallback(
    (row) => {
      if (!row?.tripRecordId) {
        errorNotify("Trip reference is unavailable for this expense.");
        return;
      }

      navigate(`/expenses/trip-expense/${row.tripRecordId}`, {
        state: {
          record: row,
        },
      });
    },
    [navigate]
  );

  const { ledgerColumns, approvalColumns } = useMemo(() => {
    const ledgerColumnsConfig = [
      {
        name: "Ledger Entry ID",
        selector: (row) => <span className="hyperlink">{row.ledgerEntryId}</span>,
        tableColumn: true,
        filterName: "ledgerEntryIdFilter",
        type: "text",
        minWidth: "180px",
      },
      {
        name: "Source Module",
        selector: (row) => row.sourceModule,
        tableColumn: true,
        filterName: "sourceModuleFilter",
        type: "text",
        minWidth: "180px",
      },
      {
        name: "Source Record ID",
        selector: (row) => <span className="hyperlink">{row.sourceRecordId}</span>,
        tableColumn: true,
        filterName: "sourceRecordIdFilter",
        type: "text",
        minWidth: "185px",
      },
      {
        name: "Trip ID",
        selector: (row) => <span className="hyperlink">{row.tripId}</span>,
        tableColumn: true,
        filterName: "tripIdFilter",
        type: "text",
        minWidth: "150px",
      },
      {
        name: "Vehicle ID",
        selector: (row) => <span className="hyperlink">{row.vehicleId}</span>,
        tableColumn: true,
        filterName: "vehicleIdFilter",
        type: "text",
        minWidth: "150px",
      },
      {
        name: "Expense Category",
        selector: (row) => row.expenseCategory,
        tableColumn: true,
        filterName: "expenseCategoryFilter",
        type: "text",
        minWidth: "180px",
      },
      {
        name: "Sub Category",
        selector: (row) => row.subCategory,
        tableColumn: true,
        filterName: "subCategoryFilter",
        type: "text",
        minWidth: "160px",
      },
      {
        name: "Amount (PKR)",
        selector: (row) => formatNumber(row.amount),
        tableColumn: true,
        filterName: "amountFilter",
        type: "number",
        minWidth: "160px",
      },
      {
        name: "Expense Date",
        selector: (row) => row.expenseDateDisplay,
        tableColumn: true,
        filterName: "expenseDateFilter",
        type: "date",
        minWidth: "160px",
      },
      {
        name: "Action",
        selector: (row) => (
          <div className="table_action_btn">
            <button
              type="button"
              className="delete_btn"
              title="Delete ledger entry"
              onClick={() =>
                setDeleteTarget({
                  id: row.id,
                  tab: EXPENSE_LEDGER_TAB,
                  label: row.ledgerEntryId,
                })
              }
            >
              <PiTrash />
            </button>
          </div>
        ),
        tableColumn: false,
        ignoreRowClick: true,
        minWidth: "90px",
      },
    ];

    const approvalColumnsConfig = [
      {
        name: "Trip ID",
        selector: (row) => (
          <span
            className={`rfq_link${row.tripRecordId ? " driver_expense_clickable" : ""}`}
            onClick={() => {
              if (!row.tripRecordId) return;
              handleOpenApprovalDetail(row);
            }}
          >
            {row.tripId}
          </span>
        ),
        tableColumn: true,
        filterName: "tripIdFilter",
        type: "text",
        minWidth: "150px",
      },
      {
        name: "Driver",
        selector: (row) => (
          <div className="rfq_requester">
            <UserAvatar username={row.driverName} image={row.driverImage} marginRight="0px" />
            <span>{row.driverName}</span>
          </div>
        ),
        tableColumn: true,
        filterName: "driverFilter",
        type: "text",
        minWidth: "220px",
      },
      {
        name: "Vehicle ID",
        selector: (row) => <span className="rfq_link">{row.vehicleId}</span>,
        tableColumn: true,
        filterName: "vehicleIdFilter",
        type: "text",
        minWidth: "150px",
      },
      {
        name: "Category",
        selector: (row) => row.category,
        tableColumn: true,
        filterName: "categoryFilter",
        type: "text",
        minWidth: "180px",
      },
      {
        name: "Sub Category",
        selector: (row) => row.subCategory,
        tableColumn: true,
        filterName: "subCategoryFilter",
        type: "text",
        minWidth: "170px",
        omit: true,
      },
      {
        name: "Amount (PKR)",
        selector: (row) => formatNumber(row.totalAmount),
        tableColumn: true,
        filterName: "amountFilter",
        type: "number",
        minWidth: "160px",
        omit: true,
      },
      {
        name: "Expense Date",
        selector: (row) => row.expenseDateDisplay,
        tableColumn: true,
        filterName: "expenseDateFilter",
        type: "date",
        minWidth: "160px",
        omit: true,
      },
      {
        name: "Total Amount (PKR)",
        selector: (row) => formatNumber(row.totalAmount),
        tableColumn: false,
        minWidth: "180px",
      },
      {
        name: "Expense Duration",
        selector: (row) => row.expenseDuration,
        tableColumn: false,
        minWidth: "170px",
      },
      {
        name: "Status",
        selector: (row) => {
          const statusMeta = approvalStatusMeta[row.status] || approvalStatusMeta.Pending;
          return (
            <span className={`rfq_approval ${statusMeta.className}`}>
              {statusMeta.icon}
              <span>{row.status}</span>
            </span>
          );
        },
        tableColumn: true,
        filterName: "statusFilter",
        type: "text",
        options: statusOptions,
        minWidth: "150px",
      },
      {
        name: "Action",
        selector: (row) => (
          <div className="driver_expense_action_cell">
            <button
              type="button"
              className="driver_expense_action_icon_btn"
              title="View expense detail"
              disabled={!row.tripRecordId}
              onClick={() => handleOpenApprovalDetail(row)}
            >
              <LuEye />
            </button>
            <span className="driver_expense_action_divider" />
            <button
              type="button"
              className="driver_expense_attachment_btn"
              title="View attachments"
              disabled={!safeArray(row.attachments).length}
              onClick={() => {
                if (!safeArray(row.attachments).length) return;
                setPreviewAttachment(row.attachments[0]);
              }}
            >
              <LuPaperclip />
              <span>{safeArray(row.attachments).length}</span>
            </button>
            <span className="driver_expense_action_divider" />
            <div className="table_action_btn">
              <button
                type="button"
                className="delete_btn"
                title="Delete approval entry"
                onClick={() =>
                  setDeleteTarget({
                    id: row.id,
                    tab: DRIVER_EXPENSE_APPROVALS_TAB,
                    label: row.tripId,
                  })
                }
              >
                <PiTrash />
              </button>
            </div>
          </div>
        ),
        tableColumn: false,
        ignoreRowClick: true,
        minWidth: "120px",
      },
    ];

    return {
      ledgerColumns: ledgerColumnsConfig,
      approvalColumns: approvalColumnsConfig,
    };
  }, [handleOpenApprovalDetail]);

  const filteredRows = useMemo(() => {
    const filterAccessors =
      activeTab === EXPENSE_LEDGER_TAB
        ? {
            ledgerEntryIdFilter: (row) => row.ledgerEntryId,
            sourceModuleFilter: (row) => row.sourceModule,
            sourceRecordIdFilter: (row) => row.sourceRecordId,
            tripIdFilter: (row) => row.tripId,
            vehicleIdFilter: (row) => row.vehicleId,
            expenseCategoryFilter: (row) => row.expenseCategory,
            subCategoryFilter: (row) => row.subCategory,
            amountFilter: (row) => row.amount,
            expenseDateFilter: (row) => row.expenseDateValue,
          }
        : {
            tripIdFilter: (row) => row.tripId,
            driverFilter: (row) => row.driverName,
            vehicleIdFilter: (row) => row.vehicleId,
            categoryFilter: (row) => row.category,
            subCategoryFilter: (row) => row.subCategory,
            amountFilter: (row) => row.totalAmount,
            expenseDateFilter: (row) => row.expenseDateValue,
            statusFilter: (row) => row.status,
          };

    return currentRows.filter((row) => {
      const searchSource =
        activeTab === EXPENSE_LEDGER_TAB
          ? [
              row.ledgerEntryId,
              row.sourceModule,
              row.sourceRecordId,
              row.tripId,
              row.vehicleId,
              row.expenseCategory,
              row.subCategory,
              row.amount,
              row.expenseDateDisplay,
            ].join(" ")
          : [
              row.tripId,
              row.driverName,
              row.vehicleId,
              row.category,
              row.subCategory,
              row.totalAmount,
              row.expenseDuration,
              row.status,
              safeArray(row.logs).map((log) => log.category).join(" "),
            ].join(" ");

      if (!matchesSearchTerm(searchSource, searchText)) {
        return false;
      }

      return Object.entries(searchFilters || {}).every(([filterName, filterMeta]) => {
        const accessor = filterAccessors[filterName];
        if (!accessor) return true;
        return matchesFilter(accessor(row), filterMeta);
      });
    });
  }, [activeTab, currentRows, searchFilters, searchText]);

  const totalItems = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * limit;
    return filteredRows.slice(startIndex, startIndex + limit);
  }, [currentPage, filteredRows, limit]);

  const statsData = useMemo(
    () =>
      activeTab === EXPENSE_LEDGER_TAB
        ? buildLedgerStatsData(ledgerEntries, approvalEntries)
        : buildApprovalStatsData(approvalEntries),
    [activeTab, approvalEntries, ledgerEntries]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget?.id) {
      errorNotify("Unable to determine the selected record.");
      return;
    }

    setDeleteLoading(true);

    try {
      if (deleteTarget.tab === EXPENSE_LEDGER_TAB) {
        await apiService.deleteTripExpense(deleteTarget.id);
        setLedgerEntries((previousRows) =>
          previousRows.filter((row) => row.id !== deleteTarget.id)
        );
      } else {
        await apiService.deletePettyCash(deleteTarget.id);
        setApprovalEntries((previousRows) =>
          previousRows.filter((row) => row.id !== deleteTarget.id)
        );
      }

      successNotify("Expense record deleted successfully");
      setDeleteModalOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      errorNotify(error || "Failed to delete expense record");
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget]);

  useEffect(() => {
    if (deleteTarget) {
      setDeleteModalOpen(true);
    }
  }, [deleteTarget]);

  const ExpandableExpenseLog = ({ data }) => {
    if (!safeArray(data?.logs).length) return null;

    return (
      <div className="driver_expense_expand_wrap" onClick={(event) => event.stopPropagation()}>
        <h5 className="driver_expense_expand_heading">Expense Log</h5>
        <div className="driver_expense_log_table_wrap">
          <table className="driver_expense_log_table">
            <thead>
              <tr>
                <th>Expense Category</th>
                <th>Expense Date</th>
                <th>Total Amount (PKR)</th>
                <th>Attachments</th>
              </tr>
            </thead>
            <tbody>
              {data.logs.map((log, index) => (
                <tr key={log.id} className={index > 0 ? "driver_expense_log_row" : ""}>
                  <td>{log.category}</td>
                  <td>{log.expenseDateDisplay}</td>
                  <td>{formatNumber(log.totalAmount)}</td>
                  <td>
                    <div className="driver_expense_log_attachment_cell">
                      <button
                        type="button"
                        className="driver_expense_attachment_btn driver_expense_attachment_btn_inline"
                        onClick={() => {
                          if (!safeArray(log.attachments).length) return;
                          setPreviewAttachment(log.attachments[0]);
                        }}
                        disabled={!safeArray(log.attachments).length}
                      >
                        <LuPaperclip />
                        <span>{safeArray(log.attachments).length}</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="driver_expense_page trip_cost_estimation_page">
      <Breadcrumbs values={["Finance", "Expenses", currentTabLabel]} />

      <div className="access_view">
        <h2>Expenses</h2>
      </div>

      <div className="driver_expense_tabs">
        <CustomTabs
          tabs={driverExpenseTabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </div>

      <Stats statsData={statsData} />

      {activeTab === DRIVER_EXPENSE_APPROVALS_TAB ? (
        <div className="procurement_table_box driver_expense_table_shell">
          <NewTableComponent
            columns={approvalColumns}
            data={paginatedRows}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            limit={limit}
            setLimit={setLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            loading={loading}
            showFilter={true}
            showPagination={true}
            limitOptions={[10]}
            searchFilters={searchFilters}
            setSearchFilters={setSearchFilters}
            onSearch={setSearchText}
            searchValue={searchText}
            expandableRows={true}
            expandableRowsComponent={ExpandableExpenseLog}
            expandableRowDisabled={(row) => !safeArray(row.logs).length}
            expandableIcon={{
              collapsed: <IoMdArrowDropright />,
              expanded: <IoMdArrowDropright style={{ transform: "rotate(90deg)" }} />,
            }}
            tableClassName="driver_expense_table driver_expense_approval_table"
            noDataComponent={
              <div className="table_no_records">There are no expenses to display</div>
            }
          />
        </div>
      ) : (
        <NewTableComponent
          columns={ledgerColumns}
          data={paginatedRows}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          limit={limit}
          setLimit={setLimit}
          totalItems={totalItems}
          totalPages={totalPages}
          loading={loading}
          showFilter={true}
          showPagination={true}
          limitOptions={[10]}
          searchFilters={searchFilters}
          setSearchFilters={setSearchFilters}
          onSearch={setSearchText}
          searchValue={searchText}
          noDataComponent={
            <div className="table_no_records">There are no expenses to display</div>
          }
        />
      )}

      <ConfirmDeleteModal
        show={deleteModalOpen}
        onClose={() => {
          if (deleteLoading) return;
          setDeleteModalOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        title={`Delete ${deleteTarget?.tab === EXPENSE_LEDGER_TAB ? "Expense Ledger" : "Driver Expense"} record?`}
        description={`This will remove ${deleteTarget?.label || "the selected record"} from the list.`}
        confirmText="Delete"
      />

      <CustomModal
        show={Boolean(previewAttachment)}
        handleClose={() => setPreviewAttachment(null)}
        heading="Attachments"
        dialogClassName="driver_expense_attachment_modal"
      >
        <div className="driver_expense_attachment_preview">
          <img
            src={previewAttachment?.preview}
            alt={previewAttachment?.name || "Expense attachment"}
          />
        </div>

        <div className="driver_expense_attachment_footer">
          <button
            type="button"
            className="driver_expense_download_btn"
            onClick={() => {
              if (!previewAttachment?.preview) return;
              const anchor = document.createElement("a");
              anchor.href = previewAttachment.preview;
              anchor.download = previewAttachment.name || "attachment";
              anchor.click();
            }}
          >
            Download
          </button>
          <button
            type="button"
            className="cancel_btn"
            onClick={() => setPreviewAttachment(null)}
          >
            Close
          </button>
        </div>
      </CustomModal>
    </div>
  );
};

export default DriverExpense;
